import { Link } from 'react-router-dom';
import { Award, CheckCircle, ArrowRight, Star, GraduationCap, BookOpen, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const RangoCard = ({ nombre, nivel, descripcion, requisitos, horas, color }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`rango-card-${nivel}`}>
    <div className="md:flex">
      <div className={`${color} text-white p-6 md:p-8 md:w-2/5 relative overflow-hidden`}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <Award className="w-7 h-7" />
            </div>
            <div>
              <p className="text-sm opacity-80 font-medium">Nivel {nivel}</p>
              <h2 className="font-manrope font-bold text-xl">{nombre}</h2>
            </div>
          </div>
          <p className="text-white/90 text-sm leading-relaxed">{descripcion}</p>
          {horas && (
            <div className="mt-4 flex items-center gap-2 text-white/80 text-sm">
              <Clock className="w-4 h-4" />
              <span>{horas}</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 md:p-8 md:w-3/5 bg-gradient-to-br from-white to-slate-50">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center text-sm">
          <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
          Requisitos de Formación
        </h3>
        <ul className="space-y-3">
          {requisitos.split('|').map((req, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
              <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-slate-500">{i + 1}</span>
              </div>
              <span>{req.trim()}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6 pt-4 border-t border-slate-100">
          <Link to="/oferta-academica">
            <Button variant="outline" size="sm" className="text-slate-700 border-slate-200 hover:bg-slate-50">
              <BookOpen className="w-4 h-4 mr-2" />
              Ver formación requerida
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

export const RangosPage = () => {
  return (
    <div className="page-transition bg-slate-50 min-h-screen" data-testid="rangos-page">
      {/* Hero Section */}
      <div className="relative bg-[#111111] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1664908402315-3f9ecc25688f?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Llaves inmobiliarias"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/60 to-[#111111]" />
        </div>
        <div className="container-cni relative py-16 md:py-24 text-center">
          <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-6">
            <GraduationCap className="w-4 h-4 mr-2" />
            Sistema de Reconocimiento Profesional
          </Badge>
          <h1 className="font-manrope font-bold text-3xl md:text-5xl text-white mb-6">
            Rangos del Colegio Nacional<br />de Inmobiliarios
          </h1>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            Nuestro sistema de rangos reconoce la formación académica, experiencia y trayectoria 
            de los profesionales inmobiliarios, estableciendo un estándar de excelencia en el sector.
          </p>
        </div>
      </div>

      {/* Rangos Section */}
      <div className="container-cni py-16">
        <div className="space-y-8">
          <RangoCard 
            nombre="Profesional Inmobiliario"
            nivel={1}
            color="bg-slate-600"
            descripcion="Nivel de entrada para profesionales inmobiliarios que cumplen con los requisitos básicos de formación avalados por el Colegio."
            horas="Mínimo 40 horas de formación"
            requisitos="Diplomado en formación inmobiliaria avalado por el Colegio|O dos cursos de formación inmobiliaria avalados por el Colegio|Certificación estatal registrada ante la Secretaría de Economía"
          />
          
          <RangoCard 
            nombre="Profesional Inmobiliario Avanzado"
            nivel={2}
            color="bg-slate-700"
            descripcion="Nivel intermedio que demuestra mayor especialización y dedicación a la formación continua en el sector inmobiliario."
            horas="60 horas adicionales de formación"
            requisitos="Ser Profesional Inmobiliario activo|Cuatro cursos adicionales (60 horas) avalados por el Colegio|Certificación federal avalada por CONOCER"
          />
          
          <RangoCard 
            nombre="Licenciado Inmobiliario"
            nivel={3}
            color="bg-blue-800"
            descripcion="Nivel académico universitario que acredita formación profesional completa en el área inmobiliaria."
            horas="Licenciatura completa"
            requisitos="Licenciatura en inmobiliaria reconocida por el Colegio|Título profesional registrado ante la SEP|Experiencia comprobable en el sector"
          />
          
          <RangoCard 
            nombre="Máster Inmobiliario"
            nivel={4}
            color="bg-slate-800"
            descripcion="Nivel de posgrado que indica profundización en el conocimiento y la práctica inmobiliaria profesional."
            horas="120 horas de diplomados"
            requisitos="Dos diplomados inmobiliarios (120 horas) avalados por el Colegio|Experiencia mínima de 3 años en el sector|Recomendaciones profesionales verificadas"
          />
          
          <RangoCard 
            nombre="Máster Inmobiliario Avanzado"
            nivel={5}
            color="bg-slate-900"
            descripcion="Nivel avanzado de posgrado, con doble especialización o formación continua intensiva en múltiples áreas."
            horas="240+ horas de formación"
            requisitos="Ser Máster Inmobiliario activo|Otro máster adicional o 120 horas adicionales de capacitación|Contribución documentada al sector"
          />
          
          <RangoCard 
            nombre="Doctor Inmobiliario"
            nivel={6}
            color="bg-amber-800"
            descripcion="Máximo nivel académico y de investigación en el campo inmobiliario, reservado para los más distinguidos profesionales."
            horas="Tres másteres completos"
            requisitos="Tres másteres inmobiliarios avalados por el Colegio|Publicaciones o investigación en el sector|Trayectoria de excelencia comprobada"
          />
          
          <RangoCard 
            nombre="Doctor Honoris Causa"
            nivel={7}
            color="bg-gradient-to-r from-amber-700 to-amber-600"
            descripcion="Reconocimiento honorífico a la trayectoria y experiencia destacada en el sector inmobiliario mexicano."
            horas="Reconocimiento por trayectoria"
            requisitos="Mínimo 30 años de trayectoria inmobiliaria comprobable|Contribución excepcional al desarrollo del sector|Nominación y aprobación del Consejo Directivo del CNI"
          />
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-white border border-slate-200 rounded-2xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-manrope font-bold text-2xl text-slate-900 mb-4">
                ¿Quieres obtener un rango superior?
              </h2>
              <p className="text-slate-600 mb-6">
                Consulta nuestra oferta académica y encuentra los cursos, diplomados y certificaciones 
                que necesitas para avanzar en tu carrera profesional.
              </p>
              <div className="flex gap-4">
                <Link to="/oferta-academica">
                  <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Ver Oferta Académica
                  </Button>
                </Link>
                <Link to="/buscar">
                  <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold">
                    Buscar Profesionales
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758518727077-ffb66ffccced?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlb3BsZSUyMG1lZXRpbmclMjBvZmZpY2V8ZW58MHx8fHwxNzcwMTY1Mzc1fDA&ixlib=rb-4.1.0&q=85"
                alt="Profesionales inmobiliarios"
                className="rounded-xl w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
